import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import {
  ActionBarContainer,
  LeftActionBar,
  RightActionBar,
} from "@/features/work/WorkView/ActionBar";
import { useUserStore } from "@/store/userStore";
import { getUserCodeByUser } from "@/utils/checkData";
import { translateWithPartnerType } from "@/utils/partnerType";
import { Button } from "@mantine/core";
import { ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconRefresh, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PurchaseOrderStatusForm } from "../form/statusForm";
import { PurchaseOrderUpdateForm } from "../form/updateForm";
import { PurchaseOrderDetailHeaderProps } from "./purchaseOrderDetailHeader";

export const PurchaseOrderDetailActionBar = (params: PurchaseOrderDetailHeaderProps) => {
  const { data } = params;
  const userCode = getUserCodeByUser();
  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;

  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest) =>
      mutatePurchaseOrders
        .delete(params)
        .mutationFn(params as any | ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrders"]);
      },
    }
  );

  const deletePurchaseOrder = () => {
    if (window.confirm(t("message.deleteItem", { item: data?.id }))) {
      deleteMutate(
        {
          purchaseOrderId: data?.id ?? 0,
        },
        {
          onSuccess: (res) => {
            if (res.status === 200) {
              customAlert(
                t("message.deleteSuccess", {
                  item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
                }),
                t("common.deleteSuccess"),
                "green"
              );
            }
          },
          onError: (error: any) => {
            customAlert(
              error.response?.data?.message ??
                t("message.deleteFail", {
                  item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
                }),
              t("common.deleteFail"),
              "red"
            );
          },
        }
      );
    }
  };

  return (
    <ActionBarContainer>
      <LeftActionBar>
        <Button
          variant="outline"
          leftIcon={<IconRefresh />}
          onClick={() =>
            openModal(
              <PurchaseOrderStatusForm formatterProps={data} />,
              null,
              t("common.change", {
                item: translateWithPartnerType(t, "purchaseOrder.status", partnerType),
              })
            )
          }
        >
          {t("common.change", {
            item: translateWithPartnerType(t, "purchaseOrder.status", partnerType),
          })}
        </Button>
      </LeftActionBar>

      <RightActionBar>
        <Button
          color="orange"
          rightIcon={<IconEdit />}
          onClick={() =>
            openModal(
              <PurchaseOrderUpdateForm formatterProps={data} />,
              null,
              t("common.edit", {
                item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
              })
            )
          }
        >
          {t("common.edit", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          })}
        </Button>
        <Button color="red" rightIcon={<IconTrash />} onClick={deletePurchaseOrder}>
          {t("common.delete", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          })}
        </Button>
      </RightActionBar>
    </ActionBarContainer>
  );
};
